import {skipToken} from '@reduxjs/toolkit/query';
import {Action, Attributes, AttributesRow, Card, DataStatus, Separator} from 'platform/components';
import {VStack} from 'platform/foundation';

import {isEmpty, isNil} from 'ramda';

import {
  useGetManufacturerQuery,
  useGetStorageLocationByIdApiQuery,
  useGetTreeFolderPathQuery,
} from '@dms/api/core';
import {useGetArticleQuery, useGetWarehouseQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {warehouseRoutes} from '@dms/routes';
import {getArticleDetailCardPrices} from '@dms/warehouse/shared';
import {ArticleDetailCardPrices} from '@dms/warehouse/shared-components';

import {composePath, Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

interface ArticleDetailCardProps extends RequiredTestIdProps {
  warehouseId: string | Nullish;
  articleId: string | Nullish;
}

export function ArticleDetailCard(props: ArticleDetailCardProps) {
  const doesNotHaveArticleDetailContext =
    isNil(props.warehouseId) ||
    isEmpty(props.warehouseId) ||
    isNil(props.articleId) ||
    isEmpty(props.articleId);

  const {
    data: articleDetail,
    isLoading: isArticleDetailLoading,
    isFetching: isArticleDetailFetching,
    isError: hasArticleDetailError,
    refetch: refreshArticleDetail,
  } = useGetArticleQuery(
    {
      warehouseId: props.warehouseId!,
      articleId: props.articleId!,
    },
    {skip: doesNotHaveArticleDetailContext}
  );

  const doesNotHaveWarehouseContext = isNil(props.warehouseId) || isEmpty(props.warehouseId);

  const {
    data: warehouseByArticle,
    isLoading: isWarehouseByArticleLoading,
    isError: hasWarehouseByArticleError,
  } = useGetWarehouseQuery({warehouseId: props.warehouseId!}, {skip: doesNotHaveWarehouseContext});

  const {
    data: manufacturerByArticle,
    isLoading: isManufacturerByArticleLoading,
    isError: hasManufacturerByArticleError,
  } = useGetManufacturerQuery(
    {manufacturerId: articleDetail?.manufacturerId},
    {skip: isNil(articleDetail?.manufacturerId)}
  );

  const {
    data: treeFolderPathByArticle,
    isLoading: isTreeFolderPathByArticleLoading,
    isError: hasTreeFolderPathByArticleError,
  } = useGetTreeFolderPathQuery(
    // eslint-disable-next-line typescript/no-non-null-asserted-optional-chain -- Muted pre-existing violation
    {leafId: articleDetail?.treeFolder?.leafId!},
    {skip: isNil(articleDetail?.treeFolder?.leafId)}
  );

  const storageLocationId = articleDetail?.storageLocationId;

  const {
    data: storageLocationDetail,
    isLoading: isStorageLocationLoading,
    isError: hasStorageLocationError,
  } = useGetStorageLocationByIdApiQuery(storageLocationId ? {storageLocationId} : skipToken);

  // Backwards compatibility: unmigrated articles have no `storageLocationId` yet,
  // so fall back to the legacy `storageLocation` string until BE migrates the data.
  const storageLocationName = storageLocationId
    ? storageLocationDetail?.location
    : articleDetail?.storageLocation;

  const isArticleDetailCardLoading =
    isArticleDetailLoading ||
    isArticleDetailFetching ||
    isWarehouseByArticleLoading ||
    isManufacturerByArticleLoading ||
    isTreeFolderPathByArticleLoading ||
    isStorageLocationLoading;

  const hasArticleDetailCardError =
    hasArticleDetailError ||
    hasWarehouseByArticleError ||
    hasManufacturerByArticleError ||
    hasTreeFolderPathByArticleError ||
    hasStorageLocationError;

  const handleEditArticle = () => {
    window.open(
      composePath(warehouseRoutes.articleDetailOverview, {
        params: {warehouseId: props.warehouseId, id: props.articleId},
        isSearchParamsPreserved: false,
      }),
      '_blank'
    );
  };

  const prices = getArticleDetailCardPrices(articleDetail);

  const currency = warehouseByArticle?.currency;

  const actions: Action[] = [
    {
      type: 'button',
      variant: 'link',
      title: i18n.t('general.actions.refresh'),
      leftIcon: 'navigation/refresh',
      onClick: refreshArticleDetail,
    },
    {
      type: 'button',
      variant: 'link',
      title: i18n.t('entity.warehouse.actions.editArticle'),
      leftIcon: 'action/open_in_new',
      onClick: handleEditArticle,
    },
  ];

  const rows: AttributesRow[] = [
    {
      label: i18n.t('entity.warehouse.labels.name'),
      value: articleDetail?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.catalogueNumber'),
      value: articleDetail?.manufacturerNumber,
    },
    {
      label: i18n.t('entity.warehouse.labels.manufacturer'),
      value: manufacturerByArticle?.name,
    },
    {
      label: i18n.t('entity.warehouse.labels.storageLocation'),
      value: storageLocationName,
    },
    {
      label: i18n.t('entity.warehouse.labels.category'),
      value: treeFolderPathByArticle?.treeFolder?.pathName,
    },
  ];
  return (
    <Card
      variant="inlineGrey"
      title={i18n.t('entity.warehouse.labels.articleDetail')}
      actions={actions}
      isExpandable
      data-testid={props['data-testid']}
    >
      <VStack>
        <DataStatus
          isLoading={isArticleDetailCardLoading}
          isError={hasArticleDetailCardError}
          minHeight={70}
        >
          <Attributes rows={rows} data-testid={suffixTestId('articleDetailAttributes', props)} />
          <Separator spacing={3} />
          <ArticleDetailCardPrices prices={prices} currency={currency} />
        </DataStatus>
      </VStack>
    </Card>
  );
}
